import { ROLE_PERMISSIONS, ABAC_RULES, type ActionKey } from '../config/permissions';
import type { User } from '../types/services/user';
import type { RoleKey } from '../config/permissions';

export const hasRole = (user: User | null, role: RoleKey): boolean => {
  if (!user) return false;
  return Array.isArray(user.claims?.roles) && user.claims.roles.includes(role);
};

export const hasAttribute = (user: User | null, key: string, value?: unknown): boolean => {
  if (!user) return false;
  const attrs = user.claims?.attributes || {};
  if (value === undefined) return key in attrs;
  const current = (attrs as Record<string, unknown>)[key];
  return current === value;
};

// Evaluación central de permisos: combina RBAC y reglas ABAC específicas por acción/recurso
export const can = (
  user: User | null,
  action: ActionKey,
  resource: string,
  context?: Record<string, unknown>
): boolean => {
  if (!user) return false;
  if (user.status !== 'active') return false;

  const roles = user.claims?.roles || [];

  if (roles.includes('admin_general')) return true;

  // RBAC: ¿algún rol del usuario permite la acción?
  const rbacAllowed = roles.some((r) => {
    const list = ROLE_PERMISSIONS[r as RoleKey] as ActionKey[] | undefined;
    return Array.isArray(list) && list.includes(action);
  });
  if (!rbacAllowed) return false;

  // ABAC: reglas complementarias según acción/recurso (cuando aplique)
  // Ejemplo: discount.apply requiere elegibilidad según atributos
  if (action === 'discount.apply' && resource === 'calculator') {
    const attrs = user.claims?.attributes || {};
    const eligible =
      ABAC_RULES.discounts.estudiante(attrs) ||
      ABAC_RULES.discounts.academia(attrs) ||
      ABAC_RULES.discounts.maker(attrs);
    return eligible;
  }

  if (action === 'blog.article.update' && resource === 'blog') {
    const authorId = context?.authorId;
    if (roles.includes('autor')) {
      if (!authorId || String(authorId) !== String(user.id)) return false;
    }
    return true;
  }

  return true;
};
